/**
 * Авторизация админ-панели (только на стороне фронтенда).
 *
 * ЧТО ЭТО ДАЁТ: закрывает интерфейс панели от случайного захода — тот, кто
 * открыл адрес, не увидит данные и не сможет ничего нажать без пароля.
 *
 * ЧЕГО НЕ ДАЁТ: это не защита API. Эндпоинты admin-backend остаются открытыми,
 * и обращение к ним напрямую (минуя браузер) проверку не проходит. Пароль в
 * каком-либо виде всё равно попадает в собранный бандл — это неизбежно для
 * проверки без участия сервера. Полноценная защита требует авторизации на
 * бэкенде; см. комментарий в конце файла.
 *
 * Что здесь всё же сделано правильно:
 *  - пароль не хранится открытым текстом: сравнивается SHA-256 от «соль+пароль»;
 *  - соль привязывает хеш к этому развёртыванию (одинаковые пароли в разных
 *    установках дают разные хеши);
 *  - сессия имеет срок жизни и протухает;
 *  - хеш и соль задаются переменными сборки, менять пароль можно без правки кода.
 */

// Значения по умолчанию можно переопределить при сборке:
//   REACT_APP_ADMIN_LOGIN, REACT_APP_ADMIN_PASSWORD_SALT, REACT_APP_ADMIN_PASSWORD_HASH
const ADMIN_LOGIN = process.env.REACT_APP_ADMIN_LOGIN || 'admin';
const PASSWORD_SALT =
  process.env.REACT_APP_ADMIN_PASSWORD_SALT || 'bd7471d241e5553766d290754eb421a4';
const PASSWORD_HASH =
  process.env.REACT_APP_ADMIN_PASSWORD_HASH ||
  '3529e66cacaf11b2fa156b9c3df0610ad663ebbffd3f8f9117c3d6f5975a01c2';

const STORAGE_KEY = 'itinterview_admin_session';

/** Срок жизни сессии — 8 часов: рабочий день без повторного входа. */
const SESSION_TTL_MS = 8 * 60 * 60 * 1000;

/** SHA-256 через встроенный Web Crypto — без сторонних зависимостей. */
async function sha256(text) {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Сравнение за постоянное время — не даёт подбирать хеш по времени ответа. */
function timingSafeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Проверяет логин и пароль.
 * @returns {Promise<boolean>}
 */
export async function checkCredentials(login, password) {
  if (!login || !password) return false;
  if (login.trim().toLowerCase() !== ADMIN_LOGIN.toLowerCase()) return false;
  const hash = await sha256(PASSWORD_SALT + password);
  return timingSafeEqual(hash, PASSWORD_HASH);
}

/** Открывает сессию после успешного входа. */
export function startSession() {
  const session = { expiresAt: Date.now() + SESSION_TTL_MS };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (e) {
    // Приватный режим браузера может запрещать запись — вход всё равно сработает,
    // просто до перезагрузки страницы.
  }
}

/** Есть ли действующая сессия. Просроченная удаляется. */
export function hasValidSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { expiresAt } = JSON.parse(raw);
    if (!expiresAt || Date.now() >= expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

/** Завершает сессию. */
export function endSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* игнорируем */
  }
}

/** Сколько осталось до конца сессии, мс (0 — сессии нет). */
export function sessionTimeLeft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const { expiresAt } = JSON.parse(raw);
    return Math.max(0, expiresAt - Date.now());
  } catch (e) {
    return 0;
  }
}

/*
 * Как усилить, когда дойдут руки: перенести проверку в admin-backend
 * (там уже есть JWT и bcrypt — используются для входа менторов) и закрыть
 * админские эндпоинты зависимостью. Сложность в том, что бот и MiniApp ходят
 * в тот же API: из 53 роутеров 29 используются ими, поэтому закрывать нужно
 * либо только «панельные» 24 роутера, либо выдать боту сервисный ключ.
 */
